import FormSection from '@/components/Forms/FormSection';
import { SOLAR_READINESS_COST_INVOICE_ERR } from '@/components/InstallMilestonePackage/InstallPackage';
import { useGetDocumentsQuery } from '@/services/documents';
import { Box, CheckboxInput, FormikTextareaInput, TextInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { InstallDocumentTypes, InstallPackageFileCategory } from 'types';

import { DocumentList } from '../../MilestonePackagePartials/Documents/DocumentList';

const solarReadinessAdderOptions = [
  {
    value: 'doubleTap',
    label: 'Double Tap',
  },
  {
    value: 'McbReplacement',
    label: 'MCB Replacement',
  },
  {
    value: 'mcbAndMountingBaseReplacement',
    label: 'MCB and Mounting Base Replacement',
  },
  {
    value: 'groundRodInstall',
    label: 'Ground Rod Installation',
  },
  {
    value: 'groundWireBtwMeterAndPanelUnder50',
    label: "Ground Wire Between Meter and Panel <50'",
  },
  {
    value: 'groundWireBtwMeterAndPanelOver50',
    label: "Ground Wire Between Meter and Panel >50'",
  },
  {
    value: 'softTrenchUnder50',
    label: "Soft Trench <50'",
  },
  {
    value: 'softTrenchOver50',
    label: "Soft Trench >50'",
  },
  {
    value: 'hardTrenchUnder50',
    label: "Hard Trench <50'",
  },
  {
    value: 'hardTrenchOver50',
    label: "Hard Trench >50'",
  },
  {
    value: 'mainPanelUpgrade',
    label: 'Main Panel Upgrade',
  },
  {
    value: 'meterUpgrade',
    label: 'Meter Upgrade',
  },
  {
    value: 'preExistingCodeViolation',
    label: 'Pre-existing Code Violation',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

interface SolarReadinessAdderProps {
  showCheckboxes: boolean;
  checkboxCategories: InstallPackageFileCategory[];
  isPackageEditable?: boolean;
}

export const SolarReadinessAdder = ({
  showCheckboxes,
  checkboxCategories,
  isPackageEditable = true,
}: SolarReadinessAdderProps) => {
  const { values, setFieldValue, setFieldError } = useFormikContext<any>();
  const [displayOtherTextArea, setDisplayOtherTextArea] = useState(
    values.solarReadiness?.otherReasonDetails ? true : false,
  );
  const [costError, setCostError] = useState<string>('');

  const { id } = useParams<{ id: string }>();

  const { data: items = [] } = useGetDocumentsQuery(
    {
      accountId: id || '',
      documentTypeFilter: [InstallDocumentTypes.solarReadinessInvoice],
    },
    { skip: !id },
  );
  const hasUploadedSolarReadinessInvoiceDoc = items?.length > 0;

  const handleSolarReadinessSelection = ({ isSelected, value }: { isSelected: boolean; value: string }) => {
    const selectedObject = solarReadinessAdderOptions.find((option) => option.value === value);

    if (!selectedObject) return;

    const currentSelectedOptions = values.solarReadiness?.reasonOptionsSelected || [];
    const updatedSelectedOptions = isSelected
      ? [...currentSelectedOptions, selectedObject]
      : currentSelectedOptions.filter((selectedOption: { value: string }) => selectedOption.value !== value);

    setFieldValue('solarReadiness.reasonOptionsSelected', updatedSelectedOptions);

    if (value === 'other') {
      setDisplayOtherTextArea(isSelected);
      if (!isSelected) {
        setFieldValue('solarReadiness.otherReasonDetails', '');
      }
    }
  };

  const handleBlurCost = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);

    if (isNaN(value)) {
      setFieldValue(`solarReadiness.cost`, 0);

      if (hasUploadedSolarReadinessInvoiceDoc) {
        setCostError(SOLAR_READINESS_COST_INVOICE_ERR);
      }
      return;
    }

    const roundedValue = Math.abs(Math.round(value / 100) * 100);
    let finalValue = roundedValue;

    if (roundedValue < 0) {
      finalValue = 0;
    } else if (roundedValue > 3000) {
      finalValue = 3000;
    }

    setFieldValue(`solarReadiness.cost`, finalValue);

    if (hasUploadedSolarReadinessInvoiceDoc && finalValue <= 0) {
      setCostError(SOLAR_READINESS_COST_INVOICE_ERR);
    } else {
      setCostError('');
    }
  };

  useEffect(() => {
    if (hasUploadedSolarReadinessInvoiceDoc && (!values?.solarReadiness?.cost || values.solarReadiness.cost <= 0)) {
      setCostError(SOLAR_READINESS_COST_INVOICE_ERR);
      setFieldError('solarReadiness.cost', SOLAR_READINESS_COST_INVOICE_ERR);
    } else {
      setCostError('');
      setFieldError('solarReadiness.cost', undefined);
    }
  }, [hasUploadedSolarReadinessInvoiceDoc, values?.solarReadiness?.cost, setFieldError]);

  return (
    <FormSection
      title="Solar Readiness Adder"
      description={'Upload evidence of the solar readiness adder for this site.'}
    >
      <TextInput
        max={3000}
        width="2xl"
        label="Amount (increments of 100)"
        name="solarReadiness.cost"
        value={values?.solarReadiness?.cost}
        id="solarReadinessCost"
        type="number"
        prefix="$"
        onChange={(event) => setFieldValue(`solarReadiness.cost`, event.target.value)}
        onBlur={handleBlurCost}
        error={costError || ''}
        isDisabled={!isPackageEditable}
      />
      <DocumentList
        documentTypeFilter={[InstallDocumentTypes.solarReadinessAdder, InstallDocumentTypes.solarReadinessInvoice]}
        title="Solar Readiness Adder"
        showTypeLabelOnItems={false}
        baseCategory={InstallDocumentTypes.solarReadinessAdder}
        showCheckboxes={showCheckboxes}
        checkboxCategories={checkboxCategories}
        isPackageEditable={isPackageEditable}
      />
      <Box height={'200px'} gap={'sm'} wrap>
        {solarReadinessAdderOptions.map((option) => (
          <CheckboxInput
            id={`readinessAdder-${option.value}`}
            label={option.label}
            name={`readinessAdder-${option.value}`}
            key={option.value}
            isChecked={values.solarReadiness?.reasonOptionsSelected?.some(
              (selectedOption: { value: string }) => selectedOption.value === option.value,
            )}
            isDisabled={!isPackageEditable}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleSolarReadinessSelection({
                isSelected: event.target.checked,
                value: option.value,
              })
            }
          />
        ))}
      </Box>
      {displayOtherTextArea && (
        <Box childGap="lg">
          <Field
            label="Other Explanation *"
            name="solarReadiness.otherReasonDetails"
            id="solarReadinessOtherReasonDetails"
            component={FormikTextareaInput}
            autoComplete="off"
            placeholder="If 'Other' is selected, provide an explanation of what work was needed"
            isDisabled={!isPackageEditable}
          />
        </Box>
      )}
    </FormSection>
  );
};
